import { NgStyle } from '@angular/common'
import { Component, DestroyRef, inject } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormsModule } from '@angular/forms'
import { FaIconComponent } from '@fortawesome/angular-fontawesome'
import { faX } from '@fortawesome/free-solid-svg-icons'
import { WjInputModule } from '@grapecity/wijmo.angular2.input'
import imageCompression, { Options } from 'browser-image-compression'
import { Observable } from 'rxjs'

import { FeatureType, LeadershipTeamVisit, ListItem, RepositoryModal, resolveCanonicalListValue } from '../../shared'
import { VoiceTextFieldComponent } from '../../shared/components/voice-text-field/voice-text-field.component'
import { LeadershipTeamVisitsListComponent } from './leadership-team-visits-list.component'

@Component({
    templateUrl: './leadership-team-visit-modal.component.html',
    standalone: true,
    imports: [FormsModule, WjInputModule, FaIconComponent, VoiceTextFieldComponent, NgStyle],
})
export class LeadershipTeamVisitModalComponent extends RepositoryModal<LeadershipTeamVisit> {
    private destroyRef = inject(DestroyRef)

    faX = faX
    jobMap: ListItem<string>[] = []
    memberMap: ListItem<string>[] = []
    images: any[] = []
    progress = 0

    protected onInitialized() {
        const parent = this.editOptions.initializeParams as LeadershipTeamVisitsListComponent
        this.jobMap = parent.jobMap
        this.memberMap = parent.memberMap
        this.target.imageUrls ??= []

        const canonicalJob = resolveCanonicalListValue(this.target.job, this.jobMap.map(j => j.value))
        if (canonicalJob != null) {
            this.target.job = canonicalJob
        }

        if (this.isAdding) {
            const currentUserId = this.services.authentication.userId()
            if (currentUserId && this.memberMap.some(m => m.value === currentUserId)) {
                this.target.visitedByUserId = currentUserId
                const match = this.memberMap.find(m => m.value === currentUserId)
                if (match) this.target.visitedByName = match.display
            }
        }
    }

    get canEdit(): boolean {
        return this.services.authentication.hasFeature([FeatureType.OperationsLeadershipTeamVisitsEdit])
    }

    onJobSelected() {
        const job = this.jobMap.find(j => j.value === this.target.job)
        this.target.jobName = job ? job.display.replace(/^\S+\s/, '') : this.target.job
    }

    onVisitedBySelected() {
        const member = this.memberMap.find(m => m.value === this.target.visitedByUserId)
        this.target.visitedByName = member ? member.display : ''
    }

    removeImage(image) {
        this.images.remove(image)
    }

    removeExistingImage(url: string) {
        const imageId = LeadershipTeamVisitModalComponent.imageIdFromUrl(url)
        if (!imageId) return

        this.services.data.http
            .delete(`api/LeadershipTeamVisit/DeleteImage?imageId=${encodeURIComponent(imageId)}`)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: () => {
                    this.target.imageUrls = (this.target.imageUrls ?? []).filter(u => u !== url)
                },
                error: errorResult => this.onError(errorResult),
            })
    }

    async onFileChanged(event) {
        const files = event.target.files
        const progress = (p: number) => {
            this.progress = p
        }
        for (const file of files) {
            const options: Options = {
                maxSizeMB: 2,
                maxWidthOrHeight: 1920,
                useWebWorker: true,
                onProgress: progress,
            }
            const compressedFile = await imageCompression(file, options)
            compressedFile['url'] = await imageCompression.getDataUrlFromFile(compressedFile)
            this.images.push(compressedFile)
        }
    }

    private uploadImages(id: string): Observable<any> {
        const uploadData = new FormData()
        uploadData.append('id', id)
        for (let i = 0; i < this.images.length; i++) {
            uploadData.append('myFile' + i, this.images[i], this.images[i].name)
        }
        return this.services.data.post('api/LeadershipTeamVisit/UploadImages', uploadData)
    }

    protected override onBeforeSave(): boolean {
        if (!this.target.job) {
            this.services.notification.warning('Job is required')
            return false
        }
        if (!this.target.visitedByUserId) {
            this.services.notification.warning('Visited By is required')
            return false
        }
        if (!this.target.visitDate) {
            this.services.notification.warning('Visit Date is required')
            return false
        }
        return true
    }

    protected onSave(resolveSave: (result?: any) => void, rejectSave: (reason?: any) => void) {
        const observable = this.isAdding || this.isDuplicating ? this.repositoryAdd() : this.repositoryEdit()
        observable.subscribe({
            next: successResult => {
                if (this.images.length === 0) {
                    resolveSave(successResult)
                    return
                }

                this.uploadImages(successResult.id).subscribe({
                    next: () => resolveSave(successResult),
                    error: errorResult => rejectSave(errorResult),
                })
            },
            error: errorResult => rejectSave(errorResult),
        })
    }

    private static imageIdFromUrl(url: string): string | null {
        const parts = url.split('/').filter(Boolean)
        if (parts.length < 2) return null
        const file = parts[1]
        const dot = file.lastIndexOf('.')
        const idPart = dot > 0 ? file.slice(0, dot) : file
        return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(idPart) ? idPart : null
    }
}
